import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { lpApi, lpErr } from "@/components/portal/lp/lpApi";
import { bigBtn, choiceBtn, gcard } from "./gedKit";
import { ListenBtn } from "./GedBits";

const SEGS = [["learn", "Learn", "Minutes 1–5"], ["try", "Try", "Minutes 6–10"], ["prove", "Prove", "Minutes 11–15"]];

function Learn({ c, voice, lid, onDone }) {
  return (
    <div className="space-y-4" data-testid="ged-seg-learn">
      <div className={`${gcard} space-y-3`}>
        <h2 className="text-2xl font-bold leading-tight">{c.headline}</h2>
        <p className="text-lg text-white/85">{c.big_idea}</p>
        <ListenBtn voice={voice} k={`lesson-${lid}`} testid="ged-learn-listen" />
      </div>
      <ol className="space-y-2">{c.steps.map((s, i) => (
        <li key={i} className="flex gap-3 rounded-2xl bg-white/[0.04] border border-white/10 p-4">
          <span className="h-9 w-9 shrink-0 grid place-items-center rounded-full bg-[#d4a017] text-black font-bold">{i + 1}</span>
          <span><span className="block font-semibold text-base">{s.label}</span><span className="text-base text-white/75">{s.text}</span></span>
        </li>
      ))}</ol>
      <div className="rounded-2xl border-2 border-sky-400/50 bg-sky-500/10 p-4 space-y-2">
        <div className="text-xs uppercase tracking-[0.25em] text-sky-300">Example</div>
        <p className="text-base font-medium">{c.example.problem}</p>
        <ol className="list-decimal pl-5 text-base text-white/80 space-y-1">{c.example.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        <p className="text-lg font-bold text-sky-200">Answer: {c.example.answer}</p>
      </div>
      <button type="button" onClick={onDone} className={bigBtn} data-testid="ged-learn-done">I've got it · Let's try</button>
    </div>
  );
}

function Ask({ q, idx, total, pick, onPick, reveal, hint }) {
  return (
    <div className={`${gcard} space-y-3`}>
      <div className="text-xs uppercase tracking-[0.25em] text-[#d4a017]">Question {idx + 1} of {total}</div>
      <p className="text-lg font-medium">{q.q}</p>
      {hint && <p className="text-sm text-white/60">Hint: {q.hint}</p>}
      <div className="space-y-2">{q.choices.map((ch, j) => (
        <button key={j} type="button" onClick={() => onPick(j)} data-testid={`ged-choice-${j}`} disabled={reveal}
          className={`${choiceBtn} ${!reveal ? (pick === j ? "border-[#d4a017] bg-[#d4a017]/15" : "border-white/15") : j === q.answer ? "border-emerald-400 bg-emerald-500/15" : j === pick ? "border-red-400 bg-red-500/15" : "border-white/10 opacity-60"}`}>{ch}</button>
      ))}</div>
    </div>
  );
}

function Try({ items, voice, onDone }) {
  const [i, setI] = useState(0);
  const [pick, setPick] = useState(null);
  const [res, setRes] = useState([]);
  const q = items[i];
  const choose = (j) => { setPick(j); const ok = j === q.answer; setRes([...res, ok]); voice.play(ok ? "right" : "wrong"); };
  const next = () => { setPick(null); i + 1 < items.length ? setI(i + 1) : onDone(res); };
  const ok = pick === q.answer;
  return (
    <div className="space-y-3" data-testid="ged-seg-try">
      <Ask q={q} idx={i} total={items.length} pick={pick} onPick={choose} reveal={pick != null} hint />
      {pick != null && <div data-testid="ged-try-feedback" className={`flex gap-2 rounded-xl p-3 text-base ${ok ? "bg-emerald-500/15" : "bg-red-500/15"}`}>
        {ok ? <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-300" /> : <XCircle className="h-6 w-6 shrink-0 text-red-300" />}<span>{ok ? "Correct! " : "Not quite. "}{q.explain}</span></div>}
      {pick != null && <button type="button" onClick={next} className={bigBtn} data-testid="ged-try-next">{i + 1 < items.length ? "Next question" : "On to Prove"}</button>}
    </div>
  );
}

function Prove({ items, onDone }) {
  const [i, setI] = useState(0);
  const [ans, setAns] = useState([]);
  const choose = (j) => { if (ans.length > i) return; const a = [...ans, j]; setAns(a); setTimeout(() => (i + 1 < items.length ? setI(i + 1) : onDone(a)), 250); };
  return (
    <div className="space-y-3" data-testid="ged-seg-prove">
      <p className="text-sm text-white/60">Rapid fire. No hints. Trust yourself.</p>
      <Ask key={i} q={items[i]} idx={i} total={items.length} pick={ans[i]} onPick={choose} reveal={false} />
    </div>
  );
}

function Done({ result, saved, onClose }) {
  return (
    <div className="text-center space-y-4 py-6" data-testid="ged-lesson-done">
      <CheckCircle2 className="h-24 w-24 mx-auto text-emerald-400 animate-in zoom-in duration-500" />
      <h2 className="text-2xl font-bold">Lesson complete</h2>
      {result ? <p className="text-lg" data-testid="ged-prove-score">Prove score: {result.score} of {result.of}</p> : saved && <p className="text-lg" data-testid="ged-prove-score">Prove score: {saved.replace("/", " of ")}</p>}
      {result && <div className="flex justify-center gap-2">{result.right.map((r, i) => <span key={i} className={`h-4 w-4 rounded-full ${r ? "bg-emerald-400" : "bg-red-400"}`} />)}</div>}
      <button type="button" onClick={onClose} className={bigBtn} data-testid="ged-lesson-close">Back to my roadmap</button>
    </div>
  );
}

export default function GedLesson({ lid, token, voice, onSummary, onClose }) {
  const api = lpApi(token);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [seg, setSeg] = useState(null);
  const [result, setResult] = useState(null);
  const load = () => { setErr(null); api.get(`/portal/resident/ged/lessons/${lid}`).then((x) => { setD(x); setSeg(SEGS.find(([k]) => !x.progress[k])?.[0] || "done"); }).catch((e) => setErr(lpErr(e))); };
  useEffect(load, [lid]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (segment, answers) => {
    try {
      const r = await api.post(`/portal/resident/ged/lessons/${lid}/segment`, { segment, answers });
      onSummary(r.summary);
      if (segment === "prove") { setResult(r); voice.play("lesson_done"); setSeg("done"); }
      else { voice.play(segment === "learn" ? "learn_done" : "try_done"); setSeg(segment === "learn" ? "try" : "prove"); }
    } catch (e) { toast.error(lpErr(e)); }
  };
  if (err) return <div className="space-y-3"><p className="text-base text-amber-200" data-testid="ged-lesson-error">{err}</p><button type="button" className={bigBtn} onClick={load}>Try again</button></div>;
  if (!d) return <div className="flex items-center gap-2 text-white/70 text-base" data-testid="ged-lesson-loading"><Loader2 className="h-5 w-5 animate-spin" /> Getting your lesson ready…</div>;
  const at = SEGS.findIndex(([k]) => k === seg);
  return (
    <div className="space-y-4" data-testid="ged-lesson">
      <div className="grid grid-cols-3 gap-2">{SEGS.map(([k, label, mins], i) => {
        const done = d.progress[k] || seg === "done" || i < at;
        return <div key={k} data-testid={`ged-seg-tab-${k}`} className={`rounded-xl p-2 text-center border-2 ${done ? "border-emerald-500 bg-emerald-500/15" : k === seg ? "border-[#d4a017] bg-[#d4a017]/15" : "border-white/10"}`}>
          <div className="font-bold text-base">{done ? "✓ " : ""}{label}</div><div className="text-[11px] text-white/60">{mins}</div></div>;
      })}</div>
      {seg === "learn" && <Learn c={d.learn} voice={voice} lid={lid} onDone={() => save("learn")} />}
      {seg === "try" && <Try items={d.try} voice={voice} onDone={(r) => save("try", r)} />}
      {seg === "prove" && <Prove items={d.prove} onDone={(a) => save("prove", a)} />}
      {seg === "done" && <Done result={result} saved={d.prove_score} onClose={onClose} />}
    </div>
  );
}
